import React, { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Card, Col, Drawer, Row, Select, Space, Statistic, Typography, message } from 'antd';
import StandardTable from '../../../components/common/StandardTable';
import ActivityDetailsShow from '../../../components/modals/ActivityDetailsShow';
import { useGetTkfResourceSummary } from '../../../hooks/genericHooks';
import type { VwTkfResourceAssignment, VwTkfResourceSummary } from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtQty = (v: unknown): string => {
  const n = toNum(v);
  if (n == null) return '';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtMoney = (v: unknown): string => {
  const n = toNum(v);
  if (n == null) return '';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const BLANK_UNIT = '__blank__';

const tokenizeAnd = (q: string): string[] => {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return [];
  return s.split(/\s+/).map((t) => t.trim()).filter(Boolean);
};

const unitValue = (unitName: unknown): string => {
  const label = String(unitName ?? '').trim();
  return label || BLANK_UNIT;
};

const unwrapRows = (resp: unknown): VwTkfResourceSummary[] => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp as VwTkfResourceSummary[];
  const obj = resp as { data?: unknown; rows?: unknown; recordset?: unknown };
  if (Array.isArray(obj.data)) return obj.data as VwTkfResourceSummary[];
  if (Array.isArray(obj.rows)) return obj.rows as VwTkfResourceSummary[];
  if (Array.isArray(obj.recordset)) return obj.recordset as VwTkfResourceSummary[];
  return [];
};

const parseAssignments = (value: VwTkfResourceSummary['assigned_tasks']): VwTkfResourceAssignment[] => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim().startsWith('[')) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const matchesTokens = (value: unknown, tokens: string[]): boolean => {
  if (!tokens.length) return true;
  const hay = String(value ?? '').toLowerCase();
  return tokens.every((t) => hay.includes(t));
};

const distinctLabels = (rows: VwTkfResourceSummary[], pick: (r: VwTkfResourceSummary) => unknown): { value: string }[] => {
  const seen = new Map<string, string>();
  for (const r of rows) {
    const label = String(pick(r) ?? '').trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (!seen.has(key)) seen.set(key, label);
  }
  return Array.from(seen.values())
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value }));
};

const ToPlnSchResourceSummary: React.FC = () => {
  const [codeSearch, setCodeSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<VwTkfResourceSummary | null>(null);
  const [activityCode, setActivityCode] = useState<string | null>(null);
  const { data: resp, isLoading, error } = useGetTkfResourceSummary();

  const rows = useMemo(() => unwrapRows(resp), [resp]);

  useEffect(() => {
    if (error) {
      message.error((error as Error)?.message || 'Failed to load resource summary');
    }
  }, [error]);

  const codeTokens = useMemo(() => tokenizeAnd(codeSearch), [codeSearch]);
  const nameTokens = useMemo(() => tokenizeAnd(nameSearch), [nameSearch]);

  const matchesCode = (r: VwTkfResourceSummary) => matchesTokens(r.rsrc_short_name, codeTokens);
  const matchesName = (r: VwTkfResourceSummary) => matchesTokens(r.rsrc_name, nameTokens);

  const codeOptions = useMemo(
    () => distinctLabels(rows.filter((r) => matchesName(r) && matchesCode(r)), (r) => r.rsrc_short_name),
    [rows, codeTokens, nameTokens]
  );

  const nameOptions = useMemo(
    () => distinctLabels(rows.filter((r) => matchesCode(r) && matchesName(r)), (r) => r.rsrc_name),
    [rows, codeTokens, nameTokens]
  );

  const unitOptions = useMemo(() => {
    const seen = new Map<string, string>();
    let hasBlank = false;
    for (const r of rows) {
      if (!matchesCode(r) || !matchesName(r)) continue;
      const value = unitValue(r.unit_name);
      if (value === BLANK_UNIT) {
        hasBlank = true;
        continue;
      }
      const key = value.toLowerCase();
      if (!seen.has(key)) seen.set(key, value);
    }
    const options = Array.from(seen.values())
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
    if (hasBlank) options.unshift({ value: BLANK_UNIT, label: '(Blank)' });
    return options;
  }, [rows, codeTokens, nameTokens]);

  useEffect(() => {
    if (!unitFilter) return;
    const stillValid = unitOptions.some((o) => o.value.toLowerCase() === unitFilter.toLowerCase());
    if (!stillValid) setUnitFilter(undefined);
  }, [unitFilter, unitOptions]);

  const filtered = useMemo(() => {
    const matched = rows.filter((r) => {
      if (!matchesCode(r) || !matchesName(r)) return false;
      if (unitFilter && unitValue(r.unit_name).toLowerCase() !== unitFilter.toLowerCase()) return false;
      return true;
    });
    return [...matched].sort((a, b) =>
      String(a.rsrc_short_name ?? '').localeCompare(String(b.rsrc_short_name ?? ''))
    );
  }, [rows, codeTokens, nameTokens, unitFilter]);

  const totalBudget = useMemo(
    () => filtered.reduce((sum, r) => sum + (toNum(r.budgeted_cost) ?? 0), 0),
    [filtered]
  );

  const allBudget = useMemo(
    () => rows.reduce((sum, r) => sum + (toNum(r.budgeted_cost) ?? 0), 0),
    [rows]
  );

  const reportedSharePct = allBudget > 0 ? (totalBudget / allBudget) * 100 : 0;

  const tableRows = filtered;
  const selectedAssignments = useMemo(
    () => parseAssignments(selected?.assigned_tasks),
    [selected]
  );

  const columns = useMemo(() => {
    return [
      {
        title: 'Resource',
        dataIndex: 'rsrc_short_name',
        key: 'rsrc_short_name',
        width: 140,
        sorter: (a: VwTkfResourceSummary, b: VwTkfResourceSummary) =>
          String(a.rsrc_short_name ?? '').localeCompare(String(b.rsrc_short_name ?? '')),
      },
      {
        title: 'Name',
        dataIndex: 'rsrc_name',
        key: 'rsrc_name',
        width: 260,
        sorter: (a: VwTkfResourceSummary, b: VwTkfResourceSummary) =>
          String(a.rsrc_name ?? '').localeCompare(String(b.rsrc_name ?? '')),
      },
      { title: 'Unit', dataIndex: 'unit_name', key: 'unit_name', width: 90 },
      {
        title: 'Activities',
        dataIndex: 'activity_count',
        key: 'activity_count',
        width: 110,
        align: 'right' as const,
        sorter: (a: VwTkfResourceSummary, b: VwTkfResourceSummary) =>
          (toNum(a.activity_count) ?? 0) - (toNum(b.activity_count) ?? 0),
      },
      {
        title: 'Budgeted Qty',
        dataIndex: 'budgeted_qty',
        key: 'budgeted_qty',
        width: 140,
        align: 'right' as const,
        render: (v: unknown) => fmtQty(v),
        sorter: (a: VwTkfResourceSummary, b: VwTkfResourceSummary) =>
          (toNum(a.budgeted_qty) ?? 0) - (toNum(b.budgeted_qty) ?? 0),
      },
      {
        title: 'Actual Qty',
        dataIndex: 'actual_qty',
        key: 'actual_qty',
        width: 140,
        align: 'right' as const,
        render: (v: unknown) => fmtQty(v),
        sorter: (a: VwTkfResourceSummary, b: VwTkfResourceSummary) =>
          (toNum(a.actual_qty) ?? 0) - (toNum(b.actual_qty) ?? 0),
      },
      {
        title: 'Budgeted Cost',
        dataIndex: 'budgeted_cost',
        key: 'budgeted_cost',
        width: 150,
        align: 'right' as const,
        render: (v: unknown) => fmtMoney(v),
        sorter: (a: VwTkfResourceSummary, b: VwTkfResourceSummary) =>
          (toNum(a.budgeted_cost) ?? 0) - (toNum(b.budgeted_cost) ?? 0),
      },
      {
        title: 'Project Weight %',
        dataIndex: 'project_weight',
        key: 'project_weight',
        width: 150,
        align: 'right' as const,
        render: (v: unknown) => `${((toNum(v) ?? 0) * 100).toFixed(3)}%`,
        sorter: (a: VwTkfResourceSummary, b: VwTkfResourceSummary) =>
          (toNum(a.project_weight) ?? 0) - (toNum(b.project_weight) ?? 0),
      },
    ];
  }, []);

  return (
    <Card style={{ margin: 16, padding: 0 }} styles={{ body: { padding: 0 } }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          padding: '16px 24px 8px 24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Resource Summary
        </Title>
        <Text type="secondary">Budgeted cost share of the project, summed across all activities</Text>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '8px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Space wrap size={12}>
          <AutoComplete
            value={codeSearch}
            onChange={(v) => setCodeSearch(v)}
            options={codeOptions}
            filterOption={false}
            allowClear
            placeholder="Resource code contains"
            style={{ width: 220 }}
            disabled={isLoading}
          />
          <AutoComplete
            value={nameSearch}
            onChange={(v) => setNameSearch(v)}
            options={nameOptions}
            filterOption={false}
            allowClear
            placeholder="Resource name contains"
            style={{ width: 260 }}
            disabled={isLoading}
          />
          <Select
            allowClear
            showSearch
            placeholder="Unit"
            value={unitFilter}
            onChange={(v) => setUnitFilter(v)}
            options={unitOptions}
            optionFilterProp="label"
            style={{ width: 160 }}
            disabled={isLoading}
          />
          <Button
            onClick={() => {
              setCodeSearch('');
              setNameSearch('');
              setUnitFilter(undefined);
            }}
            disabled={isLoading}
          >
            Reset
          </Button>
        </Space>
      </div>

      <div style={{ padding: '8px 24px 16px 24px' }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={8}>
            <Statistic title="Resources" value={tableRows.length} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Budgeted Cost" value={totalBudget} precision={2} formatter={(v) => fmtMoney(v)} />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic title="Share of all resources" value={reportedSharePct} precision={3} suffix="%" />
          </Col>
        </Row>
        <StandardTable
          columns={columns as any}
          dataSource={tableRows}
          rowKey={(r: VwTkfResourceSummary) => String(r.rsrc_id ?? r.rsrc_short_name ?? '')}
          loading={isLoading}
          enableExport
          exportFileName="resource_summary"
          scroll={{ x: 'max-content' }}
          onRow={(record: VwTkfResourceSummary) => ({
            onClick: () => setSelected(record),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      <Drawer
        title={selected ? `${selected.rsrc_short_name ?? ''} ${selected.rsrc_name ?? ''}`.trim() : 'Assigned activities'}
        placement="right"
        width={1040}
        open={selected != null}
        onClose={() => setSelected(null)}
      >
        <StandardTable
          columns={[
            { title: 'Activity', dataIndex: 'task_code', key: 'task_code', width: 140 },
            { title: 'Activity Name', dataIndex: 'task_name', key: 'task_name' },
            {
              title: 'Budgeted Qty',
              dataIndex: 'budgeted_qty',
              key: 'budgeted_qty',
              width: 140,
              align: 'right' as const,
              render: (v: unknown) => fmtQty(v),
            },
          ]}
          dataSource={selectedAssignments}
          rowKey={(r: VwTkfResourceAssignment) => String(r.task_code ?? r.task_name ?? '')}
          enableExport={true}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          onRow={(record: VwTkfResourceAssignment) => ({
            onClick: () => {
              const code = String(record.task_code ?? '').trim();
              if (code) setActivityCode(code);
            },
            style: { cursor: record.task_code ? 'pointer' : 'default' },
          })}
        />
      </Drawer>
      <ActivityDetailsShow
        open={activityCode != null}
        taskCode={activityCode}
        onClose={() => setActivityCode(null)}
      />
    </Card>
  );
};

export default ToPlnSchResourceSummary;
